import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Menu, X, Phone, Mail, Shield } from "lucide-react";
import { LogoIcon } from "../Icons";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";
import { useNavigationItems } from "../../hooks/useNavigationItems";
import { pickNav } from "../../helpers/contentHelpers";
import LanguageToggle from "../common/LanguageToggle";

// Fallback nav items when no navigation items exist in DB
const FALLBACK_NAV = [
  { key: "aboutUs", href: "about-us", linkType: "hash" },
  { key: "services", href: "benefits", linkType: "hash" },
  { key: "testimonials", href: "testimonials", linkType: "hash" },
  { key: "contactUs", href: "contact-us", linkType: "hash" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();
  const { info } = useSiteInfo();

  // Fetch dynamic navigation items
  const { items: navbarItems } = useNavigationItems("navbar-main");

  // Build nav items — from backend or fallback
  const navItems = navbarItems.length > 0
    ? navbarItems
        .filter((item) => item.isPublished !== false)
        .map((item) => {
          const picked = pickNav(item, language);
          return {
            label: picked.label,
            href: picked.url,
            linkType: picked.linkType,
            isHighlight: picked.isHighlight,
          };
        })
    : FALLBACK_NAV.map((item) => ({
        label: t(`navbar.${item.key}`),
        href: `/#${item.href}`,
        linkType: item.linkType,
      }));

  const handleCloseMenu = () => setIsOpen(false);
  const isApplyPage = location.pathname === "/apply";

  const phone = info("company_phone", t("navbar.phone"));
  const email = info("company_email", t("navbar.email"));
  const licenseNum = info("license_number", t("navbar.licenseNumber"));
  const registrationNum = info("registration_number", t("navbar.registrationNumber"));
  const businessHours = info("business_hours", t("navbar.businessHours"));

  const renderNavLink = (item, index, onClick) => {
    const baseClass = "text-xs text-slate-600 hover:text-accent-green font-bold transition-colors uppercase lg:tracking-[0.2em] md:tracking-wider whitespace-nowrap";
    const mobileClass = "block px-4 py-3 text-slate-700 hover:bg-slate-50 font-bold rounded-xl transition-colors text-sm";
    const cls = onClick ? mobileClass : baseClass;

    if (item.linkType === "hash") {
      const href = item.href?.startsWith("/#") ? item.href : `/#${(item.href || "").replace(/^#/, "")}`;
      return (
        <HashLink
          smooth
          key={index}
          to={href}
          onClick={onClick}
          className={cls}
        >
          {item.label}
        </HashLink>
      );
    }

    if (item.linkType === "external") {
      return (
        <a
          key={index}
          href={item.href || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          className={cls}
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link key={index} to={item.href || "/"} onClick={onClick} className={cls}>
        {item.label}
      </Link>
    );
  };

  return (
    <nav className="fixed w-full z-50 transition-all duration-300">
      {/* Top info bar — trust signals (Japanese design standard) */}
      <div className="bg-subidha-blue text-white text-[10px] font-bold tracking-wider hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-8">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5 text-slate-300">
              <Shield className="w-3 h-3 text-accent-green" />
              {licenseNum}
            </span>
            <span className="text-slate-500">|</span>
            <span className="text-slate-300">{registrationNum}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors">
              <Phone className="w-3 h-3 text-accent-green" />
              {phone}
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors">
              <Mail className="w-3 h-3 text-accent-green" />
              {email}
            </a>
            <span className="text-slate-400">{businessHours}</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div className="bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[4.5rem]">
            <Link to="/" className="shrink-0 group cursor-pointer">
              <LogoIcon className="h-10 w-auto rounded-md ring-1 ring-slate-200/60 group-hover:ring-slate-300 transition-all duration-200" />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center md:space-x-4 lg:space-x-6">
              {/* Hash links — only shown on employer/general page */}
              {!isApplyPage && navItems.map((item, i) => renderNavLink(item, i))}

              {/* Page switcher pills */}
              <div className="flex items-center gap-2 ml-2 border-l border-slate-200 pl-4">
                <Link
                  to="/"
                  className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
                    !isApplyPage
                      ? "bg-subidha-blue text-white"
                      : "text-slate-500 hover:text-subidha-blue"
                  }`}
                >
                  {t("navbar.forEmployers")}
                </Link>
                <Link
                  to="/apply"
                  className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
                    isApplyPage
                      ? "bg-accent-green text-white"
                      : "text-slate-500 hover:text-accent-green"
                  }`}
                >
                  {t("navbar.forCandidates")}
                </Link>
              </div>

              <LanguageToggle />
            </div>

            {/* Mobile Toggle Button */}
            <div className="md:hidden flex items-center gap-3">
              <Link
                to={isApplyPage ? "/" : "/apply"}
                className="text-[10px] font-black uppercase tracking-wider border border-subidha-blue text-subidha-blue px-3 py-1.5 rounded-lg"
              >
                {isApplyPage ? t("navbar.forEmployers") : t("navbar.forCandidates")}
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-subidha-blue p-2 hover:bg-slate-50 rounded-lg"
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
                aria-label="Toggle navigation menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          role="navigation"
          className={`md:hidden absolute top-[4.5rem] left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 transform ${
            isOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-4 opacity-0 pointer-events-none"
          }`}
        >
          <div className="px-4 pt-4 pb-8 space-y-2 shadow-2xl">
            {!isApplyPage && navItems.map((item, i) => renderNavLink(item, i, handleCloseMenu))}
            <div className="pt-2 border-t border-slate-100">
              <div className="px-4">
                <LanguageToggle />
              </div>
            </div>
          </div>
        </div>
      </div>{/* /main navbar */}
    </nav>
  );
};

export default Navbar;
